import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@iq/react-components';
import { HvSwitch } from '@hitachivantara/uikit-react-core';

import {
  getLastOpenedAt,
  getNotificationFilters,
  clearAllNotifications,
  setNotificationFilters,
  resetNotificationFilters,
  clearFilteredNotifications,
} from '../../../bundles/notifications';
import { isDoNotDisturb, clearDoNotDisturb } from '../../../bundles/notifications/doNotDisturb';
import { getActiveSiteId } from '../../../bundles/sites';
import Notification from '../Notification';
import NotificationFilter from '../../SiteAdminView/NotificationView/components/NotificationFilter';
import DoNotDisturbPanel from '../DoNotDisturbPanel';

const PANEL_FILTERS = [
  {
    key: 'sources',
    label: 'Source',
    options: [
      { value: 'system', label: 'System' },
      { value: 'alarms', label: 'Transformer Alarms' },
      { value: 'events', label: 'User Events' },
    ],
  },
  {
    key: 'types',
    label: 'Type',
    options: [
      { value: 'alert', label: 'Alert' },
      { value: 'warning', label: 'Warning' },
      { value: 'info', label: 'Info' },
      { value: 'normal', label: 'Normal' },
    ],
  },
];

const NotificationsPanel = ({ notifications, onClear, siteId }) => {
  const dispatch = useDispatch();
  const currentSiteId = useSelector(getActiveSiteId);
  const activeSiteId = siteId || currentSiteId;
  const lastOpenedAt = useSelector(getLastOpenedAt);
  const filters = useSelector(getNotificationFilters);
  const [expandedIds, setExpandedIds] = useState([]);
  const [showDnd, setShowDnd] = useState(false);
  const [dndActive, setDndActive] = useState(() => isDoNotDisturb(activeSiteId));

  useEffect(() => {
    setShowDnd(false);
    setDndActive(isDoNotDisturb(activeSiteId));
  }, [activeSiteId]);

  const filtered = useMemo(() => {
    const { sources, types } = filters;
    return notifications.filter((n) => {
      if (sources.length > 0 && !sources.includes(n.category || 'system')) return false;
      if (types.length > 0 && n.status && !types.includes(n.status)) return false;
      return true;
    });
  }, [notifications, filters]);

  const { newNotifications, oldNotifications } = useMemo(() => {
    if (!lastOpenedAt) return { newNotifications: filtered, oldNotifications: [] };
    return {
      newNotifications: filtered.filter((n) => n.createdAt > lastOpenedAt),
      oldNotifications: filtered.filter((n) => n.createdAt <= lastOpenedAt),
    };
  }, [filtered, lastOpenedAt]);

  const handleExpandNotificationGroup = (event, id) => {
    event.stopPropagation();
    event.preventDefault();
    if (notifications.some((n) => n.id === id && n.items && n.items.length > 1)) {
      if (expandedIds.includes(id)) {
        setExpandedIds((prev) => prev.filter((eid) => eid !== id));
      } else {
        setExpandedIds((prev) => [...prev, id]);
      }
    }
  };

  const handleClearAll = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (filters.sources.length > 0 || filters.types.length > 0) {
        dispatch(clearFilteredNotifications({ filters }));
      } else {
        dispatch(clearAllNotifications());
      }
    },
    [filters, dispatch]
  );

  const handleApplyFilters = (filterValues) => {
    dispatch(
      setNotificationFilters({
        sources: (filterValues.sources || []).map((o) => o.value || o),
        types: (filterValues.types || []).map((o) => o.value || o),
      })
    );
  };

  const handleResetFilters = () => {
    dispatch(resetNotificationFilters());
  };

  const buildNotifications = (notificationSubset) =>
    notificationSubset.map((notification, index) => {
      if (expandedIds.includes(notification.id)) {
        return (
          <div
            className="notifications-panel--body-expanded"
            key={`${index}${notification.createdAt}`}
          >
            <div className="collapser">
              <Button
                className="clear"
                activity="secondary"
                design="outlined"
                slim
                onClick={(event) => handleExpandNotificationGroup(event, notification.id)}
              >
                Collapse
              </Button>
            </div>
            {notification.items.map((item, index_) => (
              <Notification
                key={`${notification.id}-${index_}`}
                notification={item}
                onClear={onClear}
              />
            ))}
          </div>
        );
      }
      return (
        <Notification
          key={`${index}${notification.createdAt}`}
          notification={notification}
          onClear={onClear}
          stacked={notification.items && notification.items.length > 1}
        />
      );
    });

  const handleDndToggle = () => {
    if (dndActive) {
      clearDoNotDisturb(activeSiteId);
      setDndActive(false);
    } else {
      setShowDnd(true);
    }
  };

  return (
    <div
      className={`notifications-panel${showDnd ? ' notifications-panel--dnd-open' : ''}`}
      onClick={(event) => event.stopPropagation()}
    >
      {showDnd && (
        <DoNotDisturbPanel
          siteId={activeSiteId}
          onBack={() => {
            setShowDnd(false);
            setDndActive(isDoNotDisturb(activeSiteId));
          }}
        />
      )}

      <div className="notifications-panel--header">
        <div className="notifications-panel--header-top">
          <span className="notifications-panel--title">Notifications</span>
          {activeSiteId && (
            <div className="notifications-panel--dnd-switch">
              <HvSwitch
                checked={dndActive}
                onChange={handleDndToggle}
                aria-label="Do not disturb"
              />
              <span className="notifications-panel--dnd-label">Do not disturb</span>
            </div>
          )}
        </div>

        {activeSiteId && dndActive && (
          <div className="notifications-panel--dnd-banner">
            Your notifications are currently muted. You can unmute them by turning off the &quot;Do
            not disturb&quot; switch.
          </div>
        )}

        <div className="notifications-panel--header-actions">
          <Button
            activity="secondary"
            onClick={handleClearAll}
            disabled={notifications.length === 0}
          >
            Clear all
          </Button>
          <NotificationFilter
            filters={PANEL_FILTERS}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        </div>
      </div>

      <div className="notifications-panel--body custom-thin-scrollbar">
        {filtered.length === 0 ? (
          <div className="notifications-panel--body-empty">No notifications</div>
        ) : (
          <>
            {newNotifications.length > 0 && (
              <div className="notifications-panel--section notifications-panel--section-new">
                {buildNotifications(newNotifications)}
              </div>
            )}
            {oldNotifications.length > 0 && (
              <div className="notifications-panel--section notifications-panel--section-old">
                {buildNotifications(oldNotifications)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
